import React, { useState, useEffect, useRef } from "react";
import {
  Calendar,
  GraduationCap,
  Settings,
  ArrowLeft,
  BookOpen,
  Save,
  CheckCircle2,
  Lock,
  Loader2,
  ChevronDown,
  Check
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "react-query";
import axios from "axios";
import { BASE_URL } from "../../../services/configUrls";
import FdpView from "./FdpView";
import TechCampView from "./TechCampView";

export const HISTORY_PERMISSIONS_STORAGE_KEY = "history_role_access_config";

const EventHistory = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("fdp"); // "fdp" | "techcamp" | "access"
  const [selectedRoleId, setSelectedRoleId] = useState(null);

  // Form draft toggle states for currently selected role
  const [formFdp, setFormFdp] = useState(false);
  const [formTechcamp, setFormTechcamp] = useState(false);

  // Dropdown open/close state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const [toastMessage, setToastMessage] = useState(null);

  const api = axios.create({
    baseURL: BASE_URL,
    headers: { "Content-Type": "application/json" },
  });

  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("accessToken");
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const localRole = (localStorage.getItem("Authorise") || "staff").toLowerCase();
  const localIsAdmin = localRole === "admin";

  const hasInitializedTab = useRef(false);

  // 1. React Query: Fetch My Permissions (Runs on page load)
  const {
    data: myPermissions,
    isLoading: isMyPermsLoading,
  } = useQuery(
    ["my-event-history-permissions"],
    async () => {
      const res = await api.get("/event-history-access/my-permissions");
      return res?.data;
    },
    {
      staleTime: Infinity,
      keepPreviousData: true,
      onSuccess: (data) => {
        if (data) {
          localStorage.setItem("my_event_history_permissions", JSON.stringify(data));
          if (!hasInitializedTab.current) {
            hasInitializedTab.current = true;
            if (data.has_fdp_history) {
              setActiveTab("fdp");
            } else if (data.has_techcamp_history) {
              setActiveTab("techcamp");
            } else if (data.is_admin) {
              setActiveTab("fdp");
            }
          }
        }
      },
    }
  );

  const isAdmin = Boolean(myPermissions?.is_admin || localIsAdmin);
  const canSeeFdp = isAdmin || Boolean(myPermissions?.has_fdp_history);
  const canSeeTechcamp = isAdmin || Boolean(myPermissions?.has_techcamp_history);
  const hasAnyAccess = isAdmin || canSeeFdp || canSeeTechcamp || Boolean(myPermissions?.can_view_event_history);

  // 2. React Query: Fetch Roles (ONLY runs when in Access Management Tab)
  const {
    data: dbRoles = [],
    isLoading: isRolesLoading,
  } = useQuery(
    ["event-history-roles"],
    async () => {
      const res = await api.get("/event-history-access/roles");
      return Array.isArray(res?.data) ? res.data : [];
    },
    {
      enabled: isAdmin && activeTab === "access",
      staleTime: 300000,
      onSuccess: (roles) => {
        if (roles.length > 0 && !selectedRoleId) {
          setSelectedRoleId(roles[0].role_id);
        }
      },
    }
  );

  // 3. React Query: Fetch Permissions for Selected Role (/permissions/{role_id})
  const {
    data: selectedRolePermissions,
    isLoading: isRolePermLoading,
  } = useQuery(
    ["event-history-role-permissions", selectedRoleId],
    async () => {
      const res = await api.get(`/event-history-access/permissions/${selectedRoleId}`);
      return res?.data;
    },
    {
      enabled: Boolean(isAdmin && activeTab === "access" && selectedRoleId),
      staleTime: 30000,
    }
  );

  // Sync form toggles whenever selected role permissions change or are fetched
  useEffect(() => {
    if (selectedRolePermissions) {
      setFormFdp(Boolean(selectedRolePermissions.has_fdp_history));
      setFormTechcamp(Boolean(selectedRolePermissions.has_techcamp_history));
    }
  }, [selectedRolePermissions]);

  // 4. React Query Mutation: Save Role Permissions
  const saveMutation = useMutation(
    async ({ roleId, fdp, techcamp }) => {
      const res = await api.post("/event-history-access/permissions", {
        role_id: Number(roleId),
        has_fdp_history: Boolean(fdp),
        has_techcamp_history: Boolean(techcamp),
      });
      return res?.data;
    },
    {
      onSuccess: (data, variables) => {
        // Update query cache immediately with the saved values
        queryClient.setQueryData(
          ["event-history-role-permissions", variables.roleId],
          (old) => ({
            ...(old || {}),
            role_id: Number(variables.roleId),
            has_fdp_history: Boolean(variables.fdp),
            has_techcamp_history: Boolean(variables.techcamp),
          })
        );

        queryClient.invalidateQueries(["event-history-role-permissions", variables.roleId]);

        // Update local cache
        const selectedRoleObj = dbRoles.find((r) => r.role_id === Number(variables.roleId));
        if (selectedRoleObj) {
          try {
            const savedMap = JSON.parse(localStorage.getItem(HISTORY_PERMISSIONS_STORAGE_KEY) || "{}");
            savedMap[selectedRoleObj.role_name.toLowerCase()] = {
              has_fdp_history: variables.fdp,
              has_techcamp_history: variables.techcamp,
            };
            localStorage.setItem(HISTORY_PERMISSIONS_STORAGE_KEY, JSON.stringify(savedMap));
          } catch (e) { }
        }

        // Broadcast to sidebar
        window.dispatchEvent(new CustomEvent("historyAccessPermissionsUpdated"));

        // Keep form toggles in sync with saved values
        setFormFdp(Boolean(variables.fdp));
        setFormTechcamp(Boolean(variables.techcamp));

        setToastMessage(data?.detail || "Permissions updated successfully!");
        setTimeout(() => setToastMessage(null), 3500);
      },
      onError: (err) => {
        console.error("Error saving permissions:", err);
        setToastMessage("Failed to save permissions. Please try again.");
        setTimeout(() => setToastMessage(null), 3500);
      },
    }
  );

  const handleSavePermissions = () => {
    if (!selectedRoleId) return;
    saveMutation.mutate({
      roleId: selectedRoleId,
      fdp: formFdp,
      techcamp: formTechcamp,
    });
  };

  // Enable All Handler
  const handleEnableAll = () => {
    setFormFdp(true);
    setFormTechcamp(true);
  };

  const selectedRoleObj = dbRoles.find((r) => r.role_id === Number(selectedRoleId));
  const currentSelectedRoleName = selectedRoleObj?.role_name || "Select Role";

  if (isMyPermsLoading && !myPermissions) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex flex-col items-center justify-center p-8 space-y-3">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        <p className="text-sm font-medium text-gray-500">Loading Event History...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] p-4 md:p-6">
      {/* Toast Notification */}
      <AnimatePresence>
        {toastMessage && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-20 right-6 z-50 flex items-center gap-2.5 bg-emerald-600 text-white px-5 py-3 rounded-xl shadow-xl border border-emerald-400 text-sm font-medium"
          >
            <CheckCircle2 className="w-5 h-5 text-white" />
            <span>{toastMessage}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto space-y-6">
        {/* === Top Navigation Tabs === */}
        {hasAnyAccess && (
          <div className="bg-white rounded-xl shadow-xs border border-gray-200/80 px-6 py-2">
            <div className="flex flex-wrap items-center gap-6">
              {/* Tab 1: FDP */}
              {canSeeFdp && (
                <button
                  onClick={() => setActiveTab("fdp")}
                  className={`flex items-center gap-2 py-3 px-2 font-medium text-sm transition-all relative ${activeTab === "fdp"
                    ? "text-blue-600 font-semibold"
                    : "text-gray-600 hover:text-blue-600"
                    }`}
                >
                  <Calendar className="w-4 h-4" />
                  <span>FDP</span>
                  {activeTab === "fdp" && (
                    <motion.div
                      layoutId="activeTabIndicator"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full"
                    />
                  )}
                </button>
              )}

              {/* Tab 2: Tech Camp */}
              {canSeeTechcamp && (
                <button
                  onClick={() => setActiveTab("techcamp")}
                  className={`flex items-center gap-2 py-3 px-2 font-medium text-sm transition-all relative ${activeTab === "techcamp"
                    ? "text-purple-600 font-semibold"
                    : "text-gray-600 hover:text-purple-600"
                    }`}
                >
                  <GraduationCap className="w-4 h-4 text-purple-600" />
                  <span>Tech Camp</span>
                  {activeTab === "techcamp" && (
                    <motion.div
                      layoutId="activeTabIndicator"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600 rounded-full"
                    />
                  )}
                </button>
              )}

              {/* Tab 3: Access Management (Admin Only) */}
              {isAdmin && (
                <button
                  onClick={() => setActiveTab("access")}
                  className={`flex items-center gap-2 py-3 px-2 font-medium text-sm transition-all relative ${activeTab === "access"
                    ? "text-blue-600 font-semibold"
                    : "text-gray-600 hover:text-blue-600"
                    }`}
                >
                  <Settings className="w-4 h-4 text-blue-600" />
                  <span>Access Management</span>
                  {activeTab === "access" && (
                    <motion.div
                      layoutId="activeTabIndicator"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full"
                    />
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* === ACCESS RESTRICTED SCREEN (If role has not been granted access) === */}
        {!hasAnyAccess && (
          <div className="bg-white rounded-2xl border border-gray-200/90 shadow-sm p-12 text-center max-w-lg mx-auto my-12 space-y-4">
            <div className="w-16 h-16 bg-amber-50 border border-amber-200 rounded-full flex items-center justify-center mx-auto text-amber-600">
              <Lock className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Event History Access Required</h3>
            <p className="text-sm text-gray-500 leading-relaxed">
              Your role (<strong>{localRole}</strong>) currently does not have access to view Event History. Access must be enabled by an Administrator via the Access Management console.
            </p>
          </div>
        )}

        {/* === TAB 1 CONTENT: FDP History === */}
        {activeTab === "fdp" && canSeeFdp && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <FdpView />
          </motion.div>
        )}

        {/* === TAB 2 CONTENT: TechCamp History === */}
        {activeTab === "techcamp" && canSeeTechcamp && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <TechCampView />
          </motion.div>
        )}

        {/* === TAB 3 CONTENT: Access Management (Admin Only) === */}
        {activeTab === "access" && isAdmin && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-2xl border border-gray-200/90 shadow-sm p-6 md:p-8 space-y-8"
          >
            {/* Header with Back Arrow */}
            <div className="flex items-start gap-4">
              <button
                onClick={() => setActiveTab("fdp")}
                className="p-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-700 transition"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                  Event History Access Management
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  Configure role based access for FDP History and TechCamp History.
                </p>
              </div>
            </div>

            {/* Section 1: Select User Role (Always Opens Downwards) */}
            <div className="space-y-3">
              <label className="block text-sm font-bold text-gray-900">
                1. Select User Role
              </label>

              {isRolesLoading ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-3">
                  <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                  <span>Loading roles...</span>
                </div>
              ) : (
                <div className="relative max-w-md" ref={dropdownRef}>
                  {/* Dropdown Trigger Box */}
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className={`w-full flex items-center justify-between px-4 py-3.5 bg-white border rounded-xl text-left transition-all duration-150 ${isDropdownOpen
                      ? "border-blue-500 ring-2 ring-blue-500/10 shadow-xs"
                      : "border-gray-300 hover:border-gray-400 shadow-xs"
                      }`}
                  >
                    <span className="text-sm font-medium text-gray-900">
                      {currentSelectedRoleName}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isDropdownOpen ? "rotate-180 text-blue-600" : ""
                        }`}
                    />
                  </button>

                  {/* Downward Dropdown Menu */}
                  <AnimatePresence>
                    {isDropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 4 }}
                        transition={{ duration: 0.12 }}
                        className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-56 overflow-y-auto p-1.5 space-y-0.5"
                      >
                        {dbRoles.map((r) => {
                          const isSelected = r.role_id === Number(selectedRoleId);
                          return (
                            <button
                              key={r.role_id}
                              type="button"
                              onClick={() => {
                                if (r.role_id === Number(selectedRoleId)) {
                                  if (selectedRolePermissions) {
                                    setFormFdp(Boolean(selectedRolePermissions.has_fdp_history));
                                    setFormTechcamp(Boolean(selectedRolePermissions.has_techcamp_history));
                                  }
                                } else {
                                  setSelectedRoleId(r.role_id);
                                }
                                setIsDropdownOpen(false);
                              }}
                              className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-lg text-left text-sm transition-colors ${isSelected
                                ? "bg-blue-50 text-blue-700 font-semibold"
                                : "text-gray-700 hover:bg-gray-50"
                                }`}
                            >
                              <span>{r.role_name}</span>
                              {isSelected && (
                                <Check className="w-4 h-4 text-blue-600 stroke-[2.5]" />
                              )}
                            </button>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>

            {/* Section 2: Set Tab Permissions */}
            <div className="space-y-4 w-full">
              <div className="flex items-center justify-between w-full">
                <label className="block text-sm font-bold text-gray-900">
                  2. Set Tab Permissions
                </label>

                {/* Enable All Toggle Switch (matches FDP and Tech Camp toggle style) */}
                <div
                  onClick={() => {
                    const isAllEnabled = formFdp && formTechcamp;
                    setFormFdp(!isAllEnabled);
                    setFormTechcamp(!isAllEnabled);
                  }}
                  className="flex items-center gap-2.5 cursor-pointer select-none group"
                >
                  <span className="text-sm font-semibold text-gray-700 group-hover:text-blue-600 transition-colors">
                    Enable All
                  </span>
                  <div
                    className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${formFdp && formTechcamp ? "bg-blue-600" : "bg-gray-300"
                      }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${formFdp && formTechcamp ? "translate-x-5" : "translate-x-0"
                        }`}
                    />
                  </div>
                </div>
              </div>

              {isRolePermLoading ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-3">
                  <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                  <span>Loading permissions for {currentSelectedRoleName}...</span>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
                  {/* Card 1: FDP Tab */}
                  <div
                    onClick={() => setFormFdp(!formFdp)}
                    className={`p-6 rounded-2xl border transition-all cursor-pointer flex flex-col justify-between h-52 select-none ${formFdp
                      ? "bg-white border-blue-200 ring-1 ring-blue-500/20 shadow-sm"
                      : "bg-white border-gray-200 hover:border-gray-300"
                      }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      {/* Blue Book Icon */}
                      <div className="w-12 h-12 rounded-2xl bg-blue-100/70 flex items-center justify-center text-blue-600 shrink-0">
                        <BookOpen className="w-6 h-6 fill-blue-600 text-blue-600" />
                      </div>

                      {/* iOS Toggle Switch */}
                      <div
                        className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${formFdp ? "bg-blue-600" : "bg-gray-300"
                          }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${formFdp ? "translate-x-5" : "translate-x-0"
                            }`}
                        />
                      </div>
                    </div>

                    {/* Text Details */}
                    <div className="space-y-1 mt-2">
                      <h3 className="text-base font-bold text-gray-900">FDP</h3>
                      <p className="text-xs text-gray-500 leading-relaxed">
                        Configure {currentSelectedRoleName.toLowerCase()} access to view FDP events, details and educators.
                      </p>
                    </div>

                    {/* Status Badge */}
                    <div className="mt-3">
                      <span
                        className={`inline-flex px-3 py-1 rounded-lg text-xs font-semibold ${formFdp
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200/60"
                          : "bg-gray-100 text-gray-500 border border-gray-200"
                          }`}
                      >
                        {formFdp ? "Access Enabled" : "Access Disabled"}
                      </span>
                    </div>
                  </div>

                  {/* Card 2: Tech Camp Tab */}
                  <div
                    onClick={() => setFormTechcamp(!formTechcamp)}
                    className={`p-6 rounded-2xl border transition-all cursor-pointer flex flex-col justify-between h-52 select-none ${formTechcamp
                      ? "bg-white border-blue-200 ring-1 ring-blue-500/20 shadow-sm"
                      : "bg-white border-gray-200 hover:border-gray-300"
                      }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      {/* Purple Graduation Cap Icon */}
                      <div className="w-12 h-12 rounded-2xl bg-purple-100/70 flex items-center justify-center text-purple-600 shrink-0">
                        <GraduationCap className="w-6 h-6 fill-purple-600 text-purple-600" />
                      </div>

                      {/* iOS Toggle Switch */}
                      <div
                        className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${formTechcamp ? "bg-blue-600" : "bg-gray-300"
                          }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${formTechcamp ? "translate-x-5" : "translate-x-0"
                            }`}
                        />
                      </div>
                    </div>

                    {/* Text Details */}
                    <div className="space-y-1 mt-2">
                      <h3 className="text-base font-bold text-gray-900">Tech Camp</h3>
                      <p className="text-xs text-gray-500 leading-relaxed">
                        Configure {currentSelectedRoleName.toLowerCase()} access to view Tech Camp events, student scores and details.
                      </p>
                    </div>

                    {/* Status Badge */}
                    <div className="mt-3">
                      <span
                        className={`inline-flex px-3 py-1 rounded-lg text-xs font-semibold ${formTechcamp
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200/60"
                          : "bg-gray-100 text-gray-500 border border-gray-200"
                          }`}
                      >
                        {formTechcamp ? "Access Enabled" : "Access Disabled"}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Bottom Actions Bar */}
            <div className="pt-6 border-t border-gray-100 flex items-center justify-end w-full">
              <button
                type="button"
                onClick={handleSavePermissions}
                disabled={saveMutation.isLoading}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold shadow-sm transition active:scale-[0.99] disabled:opacity-50"
              >
                {saveMutation.isLoading ? (
                  <Loader2 className="w-4 h-4 text-white animate-spin" />
                ) : (
                  <Save className="w-4 h-4 text-white" />
                )}
                {saveMutation.isLoading ? "Saving..." : "Save & Apply Changes"}
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default EventHistory;
